import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

/**
 * DatePickerField - Standardized date picker component
 * 
 * IMPORTANT: Always use this component for date picking in dialogs and forms.
 * Never use Calendar with initialFocus in scrollable containers - it causes jumping.
 * 
 * Keyboard shortcuts:
 * - Arrow keys: Navigate days
 * - PageUp/PageDown: Navigate months
 * - Enter: Select date
 * - Escape: Close without selecting
 */
interface DatePickerFieldProps {
  label: string;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  defaultMonth?: Date;
  disabled?: (date: Date) => boolean;
  error?: string;
  id?: string;
  required?: boolean;
  name?: string;
  showKeyboardHints?: boolean;
}

export const DatePickerField = ({
  label,
  value,
  onChange,
  placeholder = "Pick a date",
  defaultMonth,
  disabled,
  error,
  id,
  required = false,
  name,
  showKeyboardHints = true,
}: DatePickerFieldProps) => {
  return (
    <div>
      <Label htmlFor={id}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            name={name}
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !value && "text-muted-foreground",
              error && "border-destructive"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, "PPP") : <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0" 
          align="start" 
          side="bottom"
          avoidCollisions={false}
          sideOffset={4}
        >
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            defaultMonth={defaultMonth}
            disabled={disabled}
            autoFocus={false}
            showKeyboardHints={showKeyboardHints}
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>
      {error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
